/**
 * Service de cache en mémoire pour les dictionnaires
 * Évite de recharger les dictionnaires depuis IndexedDB à chaque fois
 */
class DictionaryCacheService {
  constructor() {
    this.cache = {}
    this.loading = {}
    this.initialized = false
  }

  /**
   * Initialise le cache avec tous les dictionnaires critiques
   */
  async initialize() {
    if (this.initialized) return true

    try {
      const dexieService = (await import('./dexieService')).default
      
      // Charger tous les dictionnaires en parallèle
      const [propertyTypes, visitStatuses, assistanceMandators, managerMandators] = await Promise.all([
        this.getDictionary('property_type', dexieService),
        this.getDictionary('visit_status', dexieService),
        this.getAssistanceMandators(dexieService),
        this.getManagerMandators(dexieService)
      ])

      // Stocker dans le cache
      this.cache.property_type = propertyTypes
      this.cache.visit_status = visitStatuses
      this.cache.assistanceMandators = assistanceMandators
      this.cache.managerMandators = managerMandators

      this.initialized = true
      return true
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du cache des dictionnaires:', error)
      return false
    }
  }

  /**
   * Récupère un dictionnaire (depuis le cache ou IndexedDB)
   */
  async getDictionary(name, dexieService = null) {
    // Vérifier si déjà en cache
    if (this.cache[name]) {
      return this.cache[name]
    }

    // Éviter les chargements simultanés du même dictionnaire
    if (this.loading[name]) {
      return await this.loading[name]
    }

    // Créer une promesse de chargement
    this.loading[name] = this._loadDictionary(name, dexieService)
    
    try {
      const result = await this.loading[name]
      this.cache[name] = result
      return result
    } finally {
      delete this.loading[name]
    }
  }

  async _loadDictionary(name, dexieService = null) {
    try {
      if (!dexieService) {
        dexieService = (await import('./dexieService')).default
      }

      let cachedDict = await dexieService.getDictionary(name)
      
      // Vérifier si les données sont dans la structure de l'ancienne app (avec items)
      if (cachedDict && cachedDict.items && Array.isArray(cachedDict.items)) {
        return cachedDict
      }
      
      // Convertir l'objet clé-valeur en format { items: [...] }
      if (cachedDict && Object.keys(cachedDict).length > 0) {
        const items = Object.entries(cachedDict).map(([key, value]) => ({ key, value }))
        return { items }
      }
      
      return { items: [] }
    } catch (error) {
      console.error(`Erreur lors du chargement du dictionnaire ${name}:`, error)
      return { items: [] }
    }
  }

  /**
   * Récupère les mandataires d'assistance
   */
  async getAssistanceMandators(dexieService = null) {
    const cacheKey = 'assistanceMandators'
    
    // Vérifier si déjà en cache (non vide)
    if (this.cache[cacheKey] && Object.keys(this.cache[cacheKey]).length > 0) {
      return this.cache[cacheKey]
    }

    try {
      if (!dexieService) {
        const dexieModule = await import('./dexieService')
        dexieService = dexieModule.default
        
        // Utiliser la fonction assistanceMandators exportée
        const { assistanceMandators } = dexieModule
        const mandators = await assistanceMandators().toArray()
        
        // Créer une map pour un accès rapide par ID (clés normalisées en minuscules)
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id && mandator.name) {
            mandatorsMap[mandator.id.toLowerCase()] = mandator.name
          }
        })
        
        this.cache[cacheKey] = mandatorsMap
        return mandatorsMap
      }
      
      // Fallback si dexieService est passé en paramètre
      const { assistanceMandators } = await import('./dexieService')
      const mandators = await assistanceMandators().toArray()
      
      const mandatorsMap = {}
      mandators.forEach(mandator => {
        if (mandator.id && mandator.name) {
          mandatorsMap[mandator.id.toLowerCase()] = mandator.name
        }
      })
      
      this.cache[cacheKey] = mandatorsMap
      return mandatorsMap
    } catch (error) {
      console.error('Erreur lors du chargement des assistanceMandators:', error)
      this.cache[cacheKey] = {}
      return {}
    }
  }

  /**
   * Récupère les mandataires gestionnaires
   */
  async getManagerMandators(dexieService = null) {
    const cacheKey = 'managerMandators'
    
    // Vérifier si déjà en cache (non vide)
    // Note: on vérifie Object.keys().length > 0 car un objet vide {} est truthy
    // mais signifie que le cache a été initialisé quand la table était vide.
    if (this.cache[cacheKey] && Object.keys(this.cache[cacheKey]).length > 0) {
      return this.cache[cacheKey]
    }

    try {
      if (!dexieService) {
        const dexieModule = await import('./dexieService')
        dexieService = dexieModule.default
        
        // Utiliser la fonction managerMandators exportée
        const { managerMandators } = dexieModule
        const mandators = await managerMandators().toArray()
        
        // Créer une map pour un accès rapide par ID (clés normalisées en minuscules
        // pour la comparaison case-insensitive avec les UUIDs MSSQL en majuscules)
        const mandatorsMap = {}
        mandators.forEach(mandator => {
          if (mandator.id) {
            mandatorsMap[mandator.id.toLowerCase()] = {
              name: mandator.name || '',
              division: mandator.division || ''
            }
          }
        })
        
        this.cache[cacheKey] = mandatorsMap
        return mandatorsMap
      }
      
      // Fallback si dexieService est passé en paramètre
      const { managerMandators } = await import('./dexieService')
      const mandators = await managerMandators().toArray()
      
      const mandatorsMap = {}
      mandators.forEach(mandator => {
        if (mandator.id) {
          mandatorsMap[mandator.id.toLowerCase()] = {
            name: mandator.name || '',
            division: mandator.division || ''
          }
        }
      })
      
      this.cache[cacheKey] = mandatorsMap
      return mandatorsMap
    } catch (error) {
      console.error('Erreur lors du chargement des managerMandators:', error)
      this.cache[cacheKey] = {}
      return {}
    }
  }

  /**
   * Invalide le cache d'un dictionnaire spécifique
   */
  invalidate(name) {
    delete this.cache[name]
  }

  /**
   * Invalide tout le cache
   */
  invalidateAll() {
    this.cache = {}
    this.initialized = false
  }

  /**
   * Pré-charge un dictionnaire spécifique
   */
  async preload(name) {
    return await this.getDictionary(name)
  }

  /**
   * Pré-charge tous les dictionnaires utilisés par ParcimmoList
   */
  async preloadForParcimmo() {
    return await this.initialize()
  }
}

// Export d'une instance unique (singleton)
export default new DictionaryCacheService()
